// ── program-form-modal: the create/edit program Modal (POST/PATCH programs) ──
// Wave 2 (Programs). One @vjal/ui Modal that backs both the create flow (POST
// /programs) and the edit flow (PATCH /programs/:id) — the `mode` prop only
// changes the title, the submit label, and the initial values. Presentational +
// validation-only; the server is authoritative (staff/super-admin only for
// mutations — the server re-checks and 403s, which we render honestly inline).
//
// The owning company picker and assignable-coach picker load from the EXISTING
// typed services. The API requires both on create; edit keeps the current coach
// when PATCH cannot clear it (including a deactivated coach not in the active list).
// Unavailable reads stay honest (a disabled select with a hint), never a fabricated
// replacement option.
//
// Honest write contract: a server rejection (validation / permission-denied /
// gate-closed / unavailable) is surfaced INLINE via describeProgramWriteError —
// never swallowed, never faked. The caller (the page) owns the actual mutation
// and closes + toasts ONLY after the backend confirms success.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Select } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type {
  CoachingAssignableCoachDto,
  CoachingCompanyDto,
  CoachingProgramDto,
  ListResponse
} from "../../services/coaching-dtos";
import { PROGRAM_WRITE_STATUSES, describeProgramWriteError, type ProgramWriteStatus } from "./programs-write";

const services = getCoachingServices();

/** A picker option; `unavailable` marks the stored value the live list no longer offers. */
interface ProgramPickerOption {
  id: string;
  name: string;
  unavailable?: boolean;
}

/**
 * Keeps an edited program's stored company/coach visible. Once the live list has
 * loaded without it, it is shown disabled with `missingSuffix`. While the list is
 * loading or failed nothing is known, so it shows under its own name.
 */
function withCurrentOption(
  options: ProgramPickerOption[],
  loaded: boolean,
  currentId: string | undefined,
  currentName: string | undefined,
  fallbackName: string,
  missingSuffix: string
): ProgramPickerOption[] {
  if (!currentId || options.some((option) => String(option.id) === String(currentId))) return options;
  const name = currentName?.trim() || fallbackName;
  return [
    ...options,
    loaded ? { id: currentId, name: `${name} (${missingSuffix})`, unavailable: true } : { id: currentId, name }
  ];
}

export interface ProgramFormValues {
  name: string;
  status: ProgramWriteStatus;
  companyId: string;
  coachId: string;
}

export interface ProgramFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  /** The program being edited (edit mode only) — seeds the initial values. */
  program?: CoachingProgramDto | null;
  onClose: () => void;
  /** The actual write — owned by the page (react-query mutation). */
  onSubmit: (values: ProgramFormValues) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

interface FieldErrors {
  name?: string;
  companyId?: string;
  coachId?: string;
}

function initialValues(mode: "create" | "edit", program?: CoachingProgramDto | null): ProgramFormValues {
  if (mode === "edit" && program) {
    const status = (PROGRAM_WRITE_STATUSES as readonly string[]).includes(program.status ?? "")
      ? (program.status as ProgramWriteStatus)
      : "draft";
    return {
      name: program.name ?? "",
      status,
      companyId: program.company_id ?? "",
      coachId: program.coach_id ?? ""
    };
  }
  return { name: "", status: "draft", companyId: "", coachId: "" };
}

export function ProgramFormModal({
  open,
  mode,
  program,
  onClose,
  onSubmit,
  submitting = false,
  submitError
}: ProgramFormModalProps) {
  const [values, setValues] = useState<ProgramFormValues>(() => initialValues(mode, program));
  const [errors, setErrors] = useState<FieldErrors>({});
  const formId = useId();

  // Option reads (only while the modal is open). Honest: an unavailable read
  // disables the select with a neutral hint, never a fabricated option.
  const companyQuery = useHonestQuery<ListResponse<CoachingCompanyDto>>(
    coachingQueryKeys.list("companies.list"),
    () => services.companies.list(),
    { enabled: open }
  );
  const assignableCoachQuery = useHonestQuery<ListResponse<CoachingAssignableCoachDto>>(
    coachingQueryKeys.list("users.assignableCoaches"),
    () => services.users.assignableCoaches(),
    { enabled: open }
  );

  const editing = mode === "edit";
  const companyOptions = useMemo(
    () =>
      withCurrentOption(
        (companyQuery.data?.items ?? []).map((company) => ({ id: company.id, name: company.name || "Unnamed company" })),
        companyQuery.data !== undefined,
        editing ? program?.company_id : undefined,
        program?.company_name,
        "Current company",
        "no longer active"
      ),
    [companyQuery.data, editing, program?.company_id, program?.company_name]
  );
  const coachOptions = useMemo(
    () =>
      withCurrentOption(
        (assignableCoachQuery.data?.items ?? []).map((coach) => ({ id: coach.id, name: coach.name || "Unnamed coach" })),
        assignableCoachQuery.data !== undefined,
        editing ? program?.coach_id : undefined,
        program?.coach_name,
        "Current coach",
        "no longer assignable"
      ),
    [assignableCoachQuery.data, editing, program?.coach_id, program?.coach_name]
  );

  // Seed ONLY on the open false->true transition (and when the target program
  // identity changes), never on every parent re-render — otherwise an in-progress
  // edit would be clobbered by a background re-render.
  const wasOpen = useRef(false);
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const programKey = program?.id ?? null;
    if (open && (!wasOpen.current || seededFor.current !== programKey)) {
      setValues(initialValues(mode, program));
      setErrors({});
      seededFor.current = programKey;
    }
    wasOpen.current = open;
  }, [open, mode, program]);

  const set = <K extends keyof ProgramFormValues>(key: K, value: ProgramFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!values.name.trim()) next.name = "Program name is required.";
    if (mode === "create") {
      if (!values.companyId) next.companyId = "Select a company for this program.";
      if (!values.coachId) next.coachId = "Select an assigned coach for this program.";
    }
    return next;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }
    setErrors({});
    try {
      await onSubmit({
        name: values.name.trim(),
        status: values.status,
        companyId: values.companyId,
        coachId: values.coachId
      });
      // The page closes the modal + toasts on success; nothing else to do here.
    } catch {
      // The owning mutation surfaces the error via `submitError`; swallow the
      // re-throw so the modal stays open with the inline error shown.
    }
  }

  const topError = describeProgramWriteError(submitError);
  const companyDisabled = companyQuery.posture !== null && companyQuery.posture !== "empty";
  const coachDisabled = assignableCoachQuery.posture !== null && assignableCoachQuery.posture !== "empty";

  return (
    <Modal open={open} onClose={onClose} title={mode === "create" ? "Add program" : "Edit program"}>
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4" noValidate>
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Input
          label="Program name"
          value={values.name}
          onChange={(event) => set("name", event.target.value)}
          error={errors.name}
          required
        />

        <Select
          label="Owning company"
          value={values.companyId}
          disabled={companyDisabled}
          hint={
            companyDisabled
              ? editing
                ? "Companies are unavailable right now; the current company is kept."
                : "Companies are unavailable right now, so a program can't be added yet. Try again shortly."
              : undefined
          }
          error={errors.companyId}
          onChange={(event) => set("companyId", event.target.value)}
        >
          <option value="" disabled>
            Select a company…
          </option>
          {companyOptions.map((company) => (
            <option key={company.id} value={company.id} disabled={company.unavailable}>
              {company.name}
            </option>
          ))}
        </Select>

        <Select
          label="Assigned coach"
          value={values.coachId}
          disabled={coachDisabled}
          hint={
            coachDisabled
              ? editing
                ? "Coaches are unavailable right now; the current coach is kept."
                : "Coaches are unavailable right now, so a program can't be added yet. Try again shortly."
              : undefined
          }
          error={errors.coachId}
          onChange={(event) => set("coachId", event.target.value)}
        >
          <option value="" disabled>
            Select a coach…
          </option>
          {coachOptions.map((coach) => (
            <option key={coach.id} value={coach.id} disabled={coach.unavailable}>
              {coach.name}
            </option>
          ))}
        </Select>

        {mode === "edit" && (
          <Select
            label="Lifecycle status"
            value={values.status}
            onChange={(event) => set("status", event.target.value as ProgramWriteStatus)}
            hint="Draft → Active (publish) → Archived. Use the Publish and Archive actions for confirmation steps."
          >
            {PROGRAM_WRITE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status === "draft" ? "Draft" : status === "active" ? "Active" : "Archived"}
              </option>
            ))}
          </Select>
        )}

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={submitting}>
            {mode === "create" ? "Create program" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
